import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { renderAsync } from 'docx-preview';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import Loader from './reactPdf/Loader';
import { DocxViewerToolbar } from './DocxViewerToolbar';
import { DocxSearchBar } from './DocxSearchBar';
import {
  applyHighlights,
  clearHighlights,
  setActiveHighlight,
} from './docxSearch';

interface DocxViewerProps {
  url: string;
  filename?: string;
  height?: string | number;
  hideToolbar?: boolean;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.1;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const printViaIframe = (containerEl: HTMLElement, title: string) => {
  const docxStyles = Array.from(document.styleSheets)
    .filter((s) => {
      try {
        return Array.from(s.cssRules).some((r) => r.cssText.includes('docx'));
      } catch {
        return false;
      }
    })
    .map((s) =>
      Array.from(s.cssRules)
        .map((r) => r.cssText)
        .join('\n')
    )
    .join('\n');

  const iframe = document.createElement('iframe');
  iframe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;border:none;visibility:hidden';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument!;
  doc.open();
  doc.write(
    `<!doctype html><html><head><title>${title}</title>` +
      `<style>body{margin:0;padding:16px;}${docxStyles}</style>` +
      '</head><body>' +
      containerEl.innerHTML +
      '</body></html>'
  );
  doc.close();

  iframe.contentWindow!.focus();
  iframe.contentWindow!.print();
  iframe.remove();
};

export const DocxViewer = ({
  url,
  filename = 'document.docx',
  height = '100%',
  hideToolbar = false,
}: DocxViewerProps) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [scale, setScale] = useState(1);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [matchNodes, setMatchNodes] = useState<HTMLElement[]>([]);
  const [activeMatch, setActiveMatch] = useState(0);
  const toast = useShowToast();

  useEffect(() => {
    if (!url || !containerRef.current) return;
    const container = containerRef.current;
    let cancelled = false;

    const render = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        if (cancelled) return;
        container.innerHTML = '';
        await renderAsync(blob, container, undefined, {
          className: 'docx-viewer',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          experimental: false,
        });
        const wrapper = container.querySelector<HTMLElement>(
          '.docx-viewer-wrapper'
        );
        if (wrapper) {
          wrapper.style.removeProperty('box-shadow');
          wrapper.style.background = 'white';
        }
        if (!cancelled) setIsLoading(false);
      } catch (error) {
        console.error('DocxViewer failed:', error);
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const fitToWidth = useCallback(() => {
    const wrapper = wrapperRef.current;
    const page = containerRef.current?.querySelector(
      '.docx-wrapper > section'
    ) as HTMLElement | null;
    if (!wrapper || !page) return;
    const pageWidth = page.offsetWidth;
    const wrapperWidth = wrapper.clientWidth;
    if (pageWidth > 0 && wrapperWidth > 0) {
      setScale(clamp(wrapperWidth / pageWidth - 0.02, ZOOM_MIN, ZOOM_MAX));
    }
  }, []);

  useLayoutEffect(() => {
    if (isLoading) return;
    fitToWidth();
  }, [isLoading, fitToWidth, isFullscreenOpen]);

  const handleZoomIn = () =>
    setScale((s) => clamp(s + ZOOM_STEP, ZOOM_MIN, ZOOM_MAX));

  const handleZoomOut = () =>
    setScale((s) => clamp(s - ZOOM_STEP, ZOOM_MIN, ZOOM_MAX));

  const handleDownload = () => {
    downloadFileFromUrl({
      url,
      filename,
      onSuccess: () => toast({ title: 'File downloaded' }),
      onError: () => toast({ title: 'Failed to download', status: 'error' }),
    });
  };

  useEffect(() => {
    if (!isSearchOpen) {
      clearHighlights(containerRef.current);
      setMatchNodes([]);
      setActiveMatch(0);
      return;
    }
    const nodes = applyHighlights(containerRef.current, searchQuery);
    setMatchNodes(nodes);
    setActiveMatch(0);
    if (nodes.length > 0) setActiveHighlight(nodes, 0);
  }, [searchQuery, isSearchOpen, isLoading]);

  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const goToMatch = (next: number) => {
    if (matchNodes.length === 0) return;
    const wrapped =
      ((next % matchNodes.length) + matchNodes.length) % matchNodes.length;
    setActiveMatch(wrapped);
    setActiveHighlight(matchNodes, wrapped);
  };

  const handleNextMatch = () => goToMatch(activeMatch + 1);
  const handlePrevMatch = () => goToMatch(activeMatch - 1);

  useEffect(() => {
    const onChange = () => setIsFullscreenOpen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const handleFullscreenToggle = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      viewerRef.current?.requestFullscreen();
    }
  };

  const handlePrint = () => {
    if (!containerRef.current) return;
    printViaIframe(containerRef.current, filename);
  };

  if (hasError) {
    return (
      <Flex
        w="100%"
        h={height}
        align="center"
        justify="center"
        bg="white"
        p="24px"
      >
        <KText fontSize="sm" color="secondary-700">
          Unable to render this document. Try downloading it instead.
        </KText>
      </Flex>
    );
  }

  const viewer = (
    <Flex
      ref={viewerRef}
      flexDir="column"
      w="100%"
      h="100%"
      bg="white"
      border="1px solid"
      borderColor="border-color"
      borderTopRadius="sm"
      overflow="hidden"
      sx={{ '&:fullscreen': { h: '100vh', border: 'none', borderRadius: 0 } }}
    >
      {!hideToolbar && (
        <DocxViewerToolbar
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          canZoomIn={scale < ZOOM_MAX}
          canZoomOut={scale > ZOOM_MIN}
          onDownload={handleDownload}
          onPrint={handlePrint}
          isFullscreen={isFullscreenOpen}
          onFullscreen={handleFullscreenToggle}
          onSearchClick={openSearch}
          searchSlot={
            isSearchOpen ? (
              <DocxSearchBar
                query={searchQuery}
                matchCount={matchNodes.length}
                currentIndex={activeMatch}
                onQueryChange={setSearchQuery}
                onNext={handleNextMatch}
                onPrev={handlePrevMatch}
                onClose={closeSearch}
              />
            ) : undefined
          }
        />
      )}
      <Box
        ref={wrapperRef}
        flex={1}
        overflowX="hidden"
        overflowY="auto"
        bg="white"
        position="relative"
      >
        <Loader isLoading={isLoading} />
        <Box
          ref={containerRef}
          sx={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            transition: 'transform 0.15s ease',
            '.docx-viewer-wrapper': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              p: '16px 0',
            },
            'section.docx-viewer': {
              boxShadow: 'none !important',
            },
          }}
        />
      </Box>
    </Flex>
  );

  return viewer;
};
